import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';

import { gtag } from "../..";
import { useEffect } from "react";

const FibonacciEstimation = () => {
    useEffect(() => {
        gtag('event', 'page_view', {
            page_title: 'Fibonacci Estimation',
            page_path: '/fibonacci-estimation'
        });
    });

    return (
        <div className="about">
            <Typography component="h1" variant="h1">
                Why Agile Teams Use Fibonacci Numbers for Estimation
            </Typography>
            <Card>
                <CardContent className='summary-content'>
                    <Typography component="p" variant="p">
                        Walk into any sprint planning session and you'll see the same sequence on the cards: 1, 2, 3, 5, 8, 13, 21. This isn't arbitrary — the Fibonacci sequence has become the default estimation scale in agile development because it mirrors how humans naturally perceive effort and complexity.
                    </Typography>

                    <Typography component="h2" variant="h2">
                        The Problem with Linear Scales
                    </Typography>
                    <Typography component="p" variant="p">
                        Imagine estimating tasks on a scale of 1 to 10. What's the real difference between a 7 and an 8? Most teams struggle to consistently distinguish between adjacent numbers on a linear scale. This leads to false precision — the team spends time debating whether something is a 6 or a 7 when that distinction carries no meaningful information.
                    </Typography>
                    <Typography component="p" variant="p">
                        Psychologist Ernst Weber discovered in the 19th century that humans perceive differences in proportion, not in absolute terms. A 1kg weight feels noticeably different from a 2kg weight, but you can barely tell the difference between 10kg and 11kg. This principle, known as Weber's Law, applies directly to effort estimation.
                    </Typography>

                    <Typography component="h2" variant="h2">
                        How Fibonacci Reflects Uncertainty
                    </Typography>
                    <Typography component="p" variant="p">
                        The Fibonacci sequence increases by roughly 60% at each step. This growing gap between numbers serves a purpose: as tasks get larger, our ability to estimate them precisely decreases. A developer can confidently say whether a task is 1 or 2 story points. But distinguishing between 18 and 20 points of effort is nearly impossible — and the Fibonacci sequence doesn't ask you to.
                    </Typography>
                    <Typography component="p" variant="p">
                        The jump from 8 to 13 (a gap of 5) forces teams to make a meaningful choice: "Is this task closer to 8, or is it significantly more complex and belongs at 13?" This eliminates bikeshedding over small differences and focuses the conversation on what matters.
                    </Typography>

                    <Typography component="h2" variant="h2">
                        The Modified Fibonacci Scale
                    </Typography>
                    <Typography component="p" variant="p">
                        Most agile teams don't use the pure Fibonacci sequence. The standard <a href="/what-is-planning-poker">Planning Poker</a> scale is a modified version:
                    </Typography>
                    <Typography component="p" variant="p">
                        <strong>0, 1, 2, 3, 5, 8, 13, 21</strong>
                    </Typography>
                    <Typography component="p" variant="p">
                        Some variations also include 0.5 (trivial tasks), 40 or 100 (for epics that need breaking down), and special cards:
                    </Typography>
                    <ul>
                        <li><Typography component="span" variant="body1"><strong>? (Question mark):</strong> "I don't have enough information to estimate this."</Typography></li>
                        <li><Typography component="span" variant="body1"><strong>Infinity:</strong> "This is too large to estimate as a single story."</Typography></li>
                        <li><Typography component="span" variant="body1"><strong>Coffee cup:</strong> "I need a break before I can think about this clearly."</Typography></li>
                    </ul>

                    <Typography component="h2" variant="h2">
                        What Each Number Means in Practice
                    </Typography>
                    <Typography component="p" variant="p">
                        While every team calibrates differently, here's a common interpretation using a fruit analogy popularized by Fran Berriman:
                    </Typography>
                    <ul>
                        <li><Typography component="span" variant="body1"><strong>1 — Grape:</strong> Trivial. Change a label, update a config value. You could do five of these before lunch.</Typography></li>
                        <li><Typography component="span" variant="body1"><strong>2 — Cherry:</strong> Small but requires thought. A simple bug fix with a clear root cause.</Typography></li>
                        <li><Typography component="span" variant="body1"><strong>3 — Plum:</strong> Moderate. A new API endpoint with straightforward logic and tests.</Typography></li>
                        <li><Typography component="span" variant="body1"><strong>5 — Apple:</strong> Meaningful work. A feature that touches multiple components and needs careful testing.</Typography></li>
                        <li><Typography component="span" variant="body1"><strong>8 — Coconut:</strong> Complex. Multiple moving parts, potential unknowns, may need research first.</Typography></li>
                        <li><Typography component="span" variant="body1"><strong>13 — Watermelon:</strong> Very large. Should probably be broken down into smaller stories.</Typography></li>
                        <li><Typography component="span" variant="body1"><strong>21 — Jackfruit:</strong> Epic-sized. Definitely needs decomposition before it can be worked on.</Typography></li>
                    </ul>

                    <Typography component="h2" variant="h2">
                        Alternatives to Fibonacci
                    </Typography>
                    <Typography component="p" variant="p">
                        Not all teams use Fibonacci. Some alternatives include:
                    </Typography>
                    <ul>
                        <li><Typography component="span" variant="body1"><strong>T-shirt sizes (XS, S, M, L, XL):</strong> Good for high-level roadmap estimation. Less precise but faster.</Typography></li>
                        <li><Typography component="span" variant="body1"><strong>Powers of 2 (1, 2, 4, 8, 16):</strong> Even wider gaps than Fibonacci. Forces clearer distinctions.</Typography></li>
                        <li><Typography component="span" variant="body1"><strong>Linear (1-5):</strong> Simple but suffers from the false precision problem described above.</Typography></li>
                    </ul>
                    <Typography component="p" variant="p">
                        Fibonacci remains the most popular because it strikes the right balance between granularity at the low end (where precision matters) and broad strokes at the high end (where precision is impossible).
                    </Typography>

                    <Typography component="p" variant="p">
                        Want to try Fibonacci estimation with your team? <a href="/">Start a free Scrum Poker session</a> and see how the sequence shapes better conversations.
                    </Typography>
                </CardContent>
            </Card>
        </div>
    );
}

export default FibonacciEstimation;
